/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, no-restricted-properties */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import FlipMixer from 'linced-app/mixins/flip-mixer';
import { setDiff, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import {
  byKeys,
  mapBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import {
  loadSavedPreferences,
  mapGradesByLearner,
  remapProgressionsByLearner,
} from 'linced-app/utils/data-cards';

export default class DataWallController extends Controller.extend(FlipMixer) {
  @service
  features;

  @service
  options;

  @service
  personOptions;

  @service
  router;

  @service
  session;

  @service
  strings;

  @tracked
  orientation = 'landscape';

  @tracked
  editDialog = false;

  showAttendance = true;

  showAssessments = true;

  showTopics = true;

  showGoals = true;

  @tracked
  optionsSaveInProgress = false;

  childSelector = '.learner-list > .badge-card';

  @tracked
  sortType = 'field';

  @tracked
  selectedAssessments = [];

  @tracked
  selectedModules = [];

  @tracked
  selectedProgressSubjects = [];

  @tracked
  selectedGoalSubjects = [];

  @setDiff('model.assessments', 'selectedAssessments')
  remainingAssessments;

  @computed
  get userOptions() {
    return this.personOptions.getAllDataCardOptions(this.session.person, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
  }

  @computed('goalSubjects.@each.id', 'userOptions.[]')
  get isSelectedByDefault() {
    let userOptions = this.personOptions.getAllDataCardOptions(
      this.session.person,
      ['assessmentIds', 'assessmentModuleIds', 'subjectIds', 'progressIds'],
    );
    const userSubjectIds = userOptions.subjectIds || [];
    return this.goalSubjects.reduce((acc, goalSubject) => {
      acc[goalSubject.id] = userSubjectIds.includes(goalSubject.id);
      return acc;
    }, {});
  }

  // Initialize component when model loads
  init() {
    super.init(...arguments);
    this.selectedAssessments = [];
    this.selectedModules = [];
    this.selectedProgressSubjects = [];
    this.selectedGoalSubjects = [];
  }

  // Load all saved options from personOptions service

  @computed('model.allSubjects.@each.trackable')
  get progressSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];

    return allSubjects
      .filter(
        (subject) =>
          subject.trackable &&
          subject.visibleToTeachers &&
          !isEmpty(subject.id),
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.subjectProgressions.@each.{personId,subjectId}')
  get learnerProgressions() {
    return remapProgressionsByLearner(this.model.subjectProgressions);
  }

  @computed('model.allSubjects.@each.{isTopic,visibleInMenu}')
  get goalSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];
    return allSubjects
      .filter(
        (subject) =>
          !subject.isTopic && subject.visibleToTeachers && !isEmpty(subject.id),
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.allSubjects.@each.{isTopic,visibleInMenu,subjectTypeName}')
  get groupedGoalSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];

    const filteredSubjects = allSubjects
      .filter(
        (subject) =>
          !subject.isTopic &&
          subject.visibleToTeachers &&
          !isEmpty(subject.id) &&
          subject.get('subjectTypeName') !== 'Progress', // Exclude 'Progress' type
      )
      .sort(byKeys('sequence', 'label'));

    // Group subjects by subjectTypeName
    const groupedSubjects = [];
    const subjectTypeMap = {};

    filteredSubjects.forEach((subject) => {
      const typeName = subject.get('subjectTypeName') || 'Other';

      if (!subjectTypeMap[typeName]) {
        const group = {
          typeName: typeName,
          subjects: [],
        };
        groupedSubjects.push(group);
        subjectTypeMap[typeName] = group;
      }

      subjectTypeMap[typeName].subjects.push(subject);
    });

    return groupedSubjects;
  }

  @computed('model.grades.[]')
  get learnerGrades() {
    return mapGradesByLearner(this.model.grades);
  }

  @tracked sortBy = 'fullName';
  @tracked sortDir = 'asc';

  @alias('model.group.currentLearners.length')
  numLearners;

  @computed('model.group.currentLearners.[]', 'sortDir', 'sortBy')
  get sortedLearners() {
    let { sortBy, sortDir } = this;

    // Combine all goal sources into one array for lookup
    let allGoals = [
      ...Object.values(this.sortedCoreGoals || {}).flat(),
      ...Object.values(this.sortedPriorGoals || {}).flat(),
      ...Object.values(this.sortedFutureGoals || {}).flat(),
    ];

    let learners = this.model.group.currentLearners.map((learner) => {
      let sortValue;

      if (sortBy.startsWith('goal_')) {
        let goalId = sortBy.replace('goal_', '');

        // Find the goal across all goal sources
        let goal = allGoals.find((g) => g.id === goalId);

        if (goal) {
          // Extract the relevant goal state for the learner
          // resolve the async hasMany relationship upfront.
          let goalState = resolved(goal.states).find(
            (state) => state.person_id === learner.personId,
          );

          if (goalState) {
            // Determine sorting value based on awarded state
            switch (goalState.awarded) {
              case GoalState.SECURE:
                sortValue = 'a-secure'; // use a-secure that goals are sorted by these first
                break;
              case GoalState.DEVELOPING:
                sortValue = 'developing';
                break;
              default:
                sortValue = 'not-set';
            }
          } else {
            sortValue = 'unknown'; // Default if no state matches
          }
        } else {
          sortValue = 'unknown'; // No goal found
        }
      } else {
        // Default sorting by learner's properties
        sortValue = learner[sortBy] || '';
      }

      return { learner, sortValue };
    });

    // Sort learners based on sortValue
    learners = learners
      .slice()
      .sort((a, b) => (a.sortValue || '').localeCompare(b.sortValue || ''));

    // Reverse for descending order
    return sortDir === 'asc'
      ? mapBy(learners, 'learner')
      : mapBy(learners.reverse(), 'learner');
  }

  @computed(
    'model.{currentGoal,currentGoal.states.[],group.currentLearners.[]}',
  )
  get learnersByGrade() {
    let currentGoal = this.get('model.currentGoal');
    if (!currentGoal) {
      return {};
    }

    let currentGoalId = currentGoal.goal.id;
    let learners = this.get('model.group.currentLearners') || [];
    let states = currentGoal.states || [];
    // Flatten the arrays of AdapterPopulatedRecordArrays to get all state records
    let allStateRecords = [];

    // Check if states is iterable (either array-like or has forEach method)
    if (states.forEach) {
      states.forEach((recordArray) => {
        if (recordArray && recordArray.toArray) {
          // Add all records from this array to our collection
          allStateRecords.push(...recordArray.toArray());
        } else if (Array.isArray(recordArray)) {
          allStateRecords.push(...recordArray);
        }
      });
    } else if (Array.isArray(states)) {
      allStateRecords = states;
    }

    // Filter the flattened state records by goal ID
    const goalStates = allStateRecords.filter(
      (state) => state.get('goal_id') === currentGoalId,
    );

    let groupedLearners = {};

    learners.forEach((learner) => {
      // Then find the state for this learner from the pre-filtered goal states
      let goalState = goalStates.find(
        (state) => state.person_id === learner.personId,
      );

      let grade = (goalState && goalState.grade) || 'Not yet graded';

      if (!groupedLearners[grade]) {
        groupedLearners[grade] = [];
      }

      groupedLearners[grade].push(learner);
    });

    return groupedLearners;
  }

  @computed('model.currentGoal.goal')
  get topicGoal() {
    let goal = this.get('model.currentGoal.goal') || [];
    return this.groupTopicBySubcategory(goal);
  }

  @computed('model.currentGoal.goal.linkedGoals.content.[]')
  get sortedCoreGoals() {
    let linkedGoals = this.get('model.currentGoal.goal.linkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  // Helper method to group goals by subcategory label
  groupTopicBySubcategory(goal) {
    let subcategoryLabel = goal.get('subcategory.label') || 'Other';
    let acc = [];
    if (!acc[subcategoryLabel]) {
      acc[subcategoryLabel] = [];
    }
    acc[subcategoryLabel].push(goal);
    return acc;
  }

  groupGoalsBySubcategory(goals) {
    // ensure goals have resolved upfront.
    return resolved(goals).reduce((acc, goal) => {
      let subcategoryLabel = goal.get('subcategory.label') || 'Other';

      if (!acc[subcategoryLabel]) {
        acc[subcategoryLabel] = [];
      }

      acc[subcategoryLabel].push(goal);
      return acc;
    }, {});
  }

  @action
  openDialog() {
    // Reset/reload user preferences.
    loadSavedPreferences(this, this.model, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
    this.editDialog = true;
  }

  @action
  closeDialog() {
    this.editDialog = false;
  }

  @action
  setOrientation(orientation) {
    this.orientation = orientation;
  }

  @action
  toggleSelectedModule(module) {
    if (this.selectedModules.includes(module)) {
      this.selectedModules = this.selectedModules.filter((m) => m !== module);
    } else {
      this.selectedModules = [...this.selectedModules, module];
    }
  }

  @action
  toggleSelectedProgressSubjects(subject) {
    if (this.selectedProgressSubjects.includes(subject)) {
      this.selectedProgressSubjects = this.selectedProgressSubjects.filter(
        (s) => s !== subject,
      );
    } else {
      this.selectedProgressSubjects = [
        ...this.selectedProgressSubjects,
        subject,
      ];
    }
  }

  @action
  toggleSelectedGoalSubjects(subject) {
    if (this.selectedGoalSubjects.includes(subject)) {
      this.selectedGoalSubjects = this.selectedGoalSubjects.filter(
        (s) => s !== subject,
      );
    } else {
      this.selectedGoalSubjects = [...this.selectedGoalSubjects, subject];
    }
  }

  @action
  saveSelections() {
    this.optionsSaveInProgress = true;
    let assessmentIds = this.selectedAssessments.map(
      (assessment) => assessment.id,
    );
    let moduleIds = this.selectedModules.map((module) => module.id);
    let progressSubjectIds = this.selectedProgressSubjects.map(
      (subject) => subject.id,
    );
    let goalSubjectIds = this.selectedGoalSubjects.map((subject) => subject.id);

    this.personOptions
      .updateAllDataCardOptions(this.session.person, {
        assessmentIds: assessmentIds,
        assessmentModuleIds: moduleIds,
        subjectIds: goalSubjectIds,
        progressIds: progressSubjectIds,
      })
      .then(() => {
        this.editDialog = false;
        this.router.refresh('group.topics.topic.data-wall');
      })
      .catch(() => {
        this.toaster.error('Error saving selections. Please try again.');
      })
      .finally(() => {
        this.optionsSaveInProgress = false;
      });
  }

  @action
  addAssessment(assessment) {
    this.selectedAssessments = [...this.selectedAssessments, assessment];
  }

  @action
  removeAssessment(assessment) {
    this.selectedAssessments = this.selectedAssessments.filter(
      (a) => a !== assessment,
    );
  }

  @action
  sortByField(fieldPath) {
    // Sorting by field defaults to ascending.
    let sortDir = 'asc';
    if (this.sortBy === fieldPath) {
      // Same field - invert direction.
      sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    }
    this.sortBy = fieldPath;
    this.sortType = 'field';
    this.sortDir = sortDir;
  }
}
